import { createCallable } from './functions';

// Define the types for the goal operations
interface GetRandomWordParams {
  type: 'Item' | 'Location' | 'Character';
  currentWord?: string;
  recentWords?: string[];
}

interface GetRandomWordResult {
  success: boolean;
  result?: string;
  error?: string;
}

interface GetRandomWordsParams {
  count: number;
  type: 'Item' | 'Location' | 'Character';
  excludeWords?: string[];
}

interface GetRandomWordsResult {
  success: boolean;
  result?: string[];
  error?: string;
}

// Create callable functions for goal operations (no auth required)
const proxyGetRandomWordCallable = createCallable('proxyGetRandomWord', false);
const proxyGetRandomWordsCallable = createCallable('proxyGetRandomWords', false);

/**
 * Get a random word for a goal using the Firebase Cloud Function
 * @param type The type of word to get (Item, Location, Character)
 * @param currentWord The current word to exclude
 * @param recentWords A list of recent words to exclude
 * @returns A random word
 */
export async function proxyGetRandomWord(
  type: 'Item' | 'Location' | 'Character',
  currentWord: string = '',
  recentWords: string[] = []
): Promise<string> {
  try {
    const params: GetRandomWordParams = {
      type,
      currentWord,
      recentWords,
    };

    const result = (await proxyGetRandomWordCallable(params)) as GetRandomWordResult;

    if (!result.success || !result.result) {
      throw new Error(result.error || 'Failed to get random word');
    }

    return result.result;
  } catch (error: any) {
    // Return a default word on error
    return type === 'Item' ? 'Sword' : type === 'Location' ? 'Castle' : 'Wizard';
  }
}

/**
 * Get multiple random words for goals using the Firebase Cloud Function
 * @param count The number of words to get
 * @param type The type of words to get (Item, Location, Character)
 * @param excludeWords A list of words to exclude
 * @returns An array of random words
 */
export async function getRandomWords(
  count: number,
  type: 'Item' | 'Location' | 'Character',
  excludeWords: string[] = []
): Promise<string[]> {
  //(`[goalOperations] getRandomWords called with count=${count}, type=${type}`);

  try {
    const params: GetRandomWordsParams = {
      count,
      type,
      excludeWords,
    };

    //console.log('[goalOperations] Calling Cloud Function for getRandomWords');
    const result = (await proxyGetRandomWordsCallable(params)) as GetRandomWordsResult;
    //console.log('[goalOperations] Cloud Function result:', result);

    if (!result.success || !result.result) {
      console.error('[goalOperations] Function returned error:', result.error);
      throw new Error(result.error || 'Failed to get random words');
    }

    //console.log('[goalOperations] Returning successful result:', result.result);
    return result.result;
  } catch (error: any) {
    console.error('[goalOperations] Error:', error);
    // Return default words on error
    const defaults =
      type === 'Item'
        ? ['Sword', 'Shield', 'Potion', 'Book', 'Candle']
        : type === 'Location'
        ? ['Castle', 'Forest', 'Mountain', 'Beach', 'Cave']
        : ['Wizard', 'Knight', 'Dragon', 'Princess', 'King'];

    return defaults.slice(0, count);
  }
}
